import { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { X, ChevronRight } from "lucide-react";
import taskeerLogo from "@/assets/taskeerlogo.png";

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  badge?: string;
}

interface MobileNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuItems: MenuItem[];
  onLogout?: () => void;
  logoutLabel?: string;
  extraContent?: ReactNode;
}

export const MobileNav = ({
  open,
  onOpenChange,
  menuItems,
  onLogout,
  logoutLabel = "Sair",
  extraContent,
}: MobileNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path: string) => {
    onOpenChange(false);
    // Small delay for smoother transition
    setTimeout(() => navigate(path), 150);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between border-b border-border pb-4 flex-shrink-0">
          <DrawerTitle className="flex items-center gap-2">
            <img src={taskeerLogo} alt="Taskeer" className="h-6" />
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-2">
          <nav className="flex flex-col gap-1">
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  className={cn(
                    "flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-left transition-all duration-200 active:scale-[0.98]",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted/50 text-foreground"
                  )}
                  style={{
                    animationDelay: `${index * 30}ms`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                        isActive ? "bg-primary/20" : "bg-muted/50"
                      )}
                    >
                      <item.icon
                        className={cn(
                          "w-5 h-5",
                          isActive ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                    </div>
                    <span className="font-medium">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 transition-colors",
                      isActive ? "text-primary" : "text-muted-foreground/50"
                    )}
                  />
                </button>
              );
            })}
          </nav>

          {extraContent && (
            <div className="mt-4 pt-4 border-t border-border">{extraContent}</div>
          )}
        </div>

        {onLogout && (
          <div className="p-4 border-t border-border mt-auto">
            <Button
              variant="ghost"
              onClick={() => {
                onOpenChange(false);
                onLogout();
              }}
              className="w-full justify-start h-12 px-4 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl"
            >
              <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </div>
              <span className="font-medium">{logoutLabel}</span>
            </Button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
